import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@hub/ui";
import { useAuditTour } from "./TourProvider";

// The guided-tour overlay: a dimmed page with a cutout around the step's target
// and a card explaining it. Ported from the prototype's `Walkthrough`.

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const CARD_WIDTH = 340;
/** Card height used only to decide above-vs-below placement, not to size it. */
const CARD_HEIGHT_ESTIMATE = 190;

/**
 * Tracks the on-screen box of the step's target.
 *
 * Re-measured on resize, on scroll (capture phase, so it catches scrolling
 * inside the case list rather than only the window), and on a slow interval.
 * The interval is the unglamorous part that makes it reliable: the target may
 * not exist yet when the step opens, because the step just asked the host to
 * navigate and the new view mounts a frame or two later. Polling lets the
 * spotlight land when the element appears instead of leaving the step
 * permanently centred.
 */
function useTargetRect(selector: string | undefined): Rect | null {
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (!selector) {
      setRect(null);
      return;
    }
    let cancelled = false;

    const place = () => {
      if (cancelled) return;
      const el = document.querySelector(selector);
      if (!el) {
        // Target absent — the view may still be mounting, or this step's screen
        // could not be reached. Fall back to a centred card rather than
        // spotlighting nothing.
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      // Off-screen targets get scrolled into view first, then re-measured — a
      // cutout drawn at the pre-scroll position highlights empty space.
      if (r.top < 70 || r.bottom > window.innerHeight - 40) {
        el.scrollIntoView({ block: "center", inline: "nearest" });
        const after = el.getBoundingClientRect();
        setRect({
          top: after.top,
          left: after.left,
          width: after.width,
          height: after.height,
        });
        return;
      }
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    const poll = window.setInterval(place, 250);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.clearInterval(poll);
    };
  }, [selector]);

  return rect;
}

/** Places the card near the target, clamped inside the viewport. */
function cardPosition(rect: Rect | null): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) {
    return { top: vh / 2 - 110, left: vw / 2 - CARD_WIDTH / 2 };
  }
  const fitsBelow = rect.top + rect.height + 16 + CARD_HEIGHT_ESTIMATE < vh;
  const top = fitsBelow
    ? rect.top + rect.height + 14
    : Math.max(16, rect.top - 14 - CARD_HEIGHT_ESTIMATE);
  const left = Math.min(
    Math.max(16, rect.left + rect.width / 2 - CARD_WIDTH / 2),
    vw - CARD_WIDTH - 16,
  );
  return { top, left };
}

export function Walkthrough() {
  const tour = useAuditTour();
  const rect = useTargetRect(tour?.step?.selector);

  if (!tour?.isOpen || !tour.step) return null;

  const { step, index, total, close, next, back } = tour;
  const isLast = index === total - 1;
  const pos = cardPosition(rect);

  return (
    <div
      className="fixed inset-0 z-[300]"
      role="dialog"
      aria-modal="true"
      aria-label="Guided walkthrough"
    >
      {/* The dim. With a target, one enormous spread shadow paints everything
          EXCEPT the cutout — cheaper and crisper than four positioned panels,
          and it animates as one box when the step moves. */}
      {rect ? (
        <div
          className="pointer-events-none absolute rounded-[10px] transition-all duration-200"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(9,15,25,.58)",
          }}
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: "rgba(9,15,25,.58)" }}
        />
      )}

      <div
        className="absolute z-[301] rounded-xl border bg-background p-4 shadow-2xl"
        style={{ top: pos.top, left: pos.left, width: CARD_WIDTH }}
      >
        <div className="mb-1.5 flex items-center justify-between">
          <span
            className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums"
            style={{
              background:
                "color-mix(in srgb, var(--ch-blue-500) 14%, transparent)",
              color: "var(--ch-blue-700)",
            }}
          >
            {index + 1} / {total}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={close}
            aria-label="Close walkthrough"
          >
            <X className="size-3.5" />
          </Button>
        </div>

        <div className="mb-1.5 text-[15px] font-semibold tracking-tight">
          {step.title}
        </div>
        <p className="m-0 text-[12.5px] leading-relaxed text-muted-foreground">
          {step.body}
        </p>

        <div className="mt-3.5 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={close}>
            Skip tour
          </Button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <Button variant="outline" size="sm" onClick={back}>
                Back
              </Button>
            )}
            <Button size="sm" onClick={isLast ? close : next}>
              {isLast ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
